/* eslint-disable indent */
import { FC, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { Checkbox, CheckboxType } from "components/atoms/Checkbox";
import { ADetailerModal } from "components/molecules/ADetailerModal";
import { BGRemovalModal } from "components/molecules/BGRemovalModal";
import CanvasModal from "components/molecules/CanvasModal";
import ControlsContainer from "components/molecules/ControlsContainer";
import { DeleteAllConfirmationModal } from "components/molecules/DeleteAllModal";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import { EditModal } from "components/molecules/EditModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import { FaceSwapModal } from "components/molecules/FaceSwapModal";
import { InpaintingModal } from "components/molecules/InpaintingModal";
import ShareModal from "components/molecules/ShareModal";
import { UpscalerModal } from "components/molecules/UpscalerModal";
import { deleteAllImages, deleteImage, onToggleVisibility } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, IDeleteImageRequest, ImageDTO, UserPremiumType } from "types";
import * as Styled from "./ImageViewer.styled";
type ImageViewerProps = {
  imageDtos: ImageDTO[];
  onImageClick: (image: ImageDTO) => void;
  updateImage: (image: ImageDTO) => void;
  onSubmitEdit: (editRequest: string, isPremium: boolean, imageId: string, editType: string, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File, base64Image?: string) => void;
  onVote: (imageId: string) => void;
};
export const ImageViewer: FC<ImageViewerProps> = ({
  imageDtos,
  onImageClick,
  onSubmitEdit,
  onVote
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [images, setImages] = useState<ImageDTO[]>(imageDtos);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editImageId, setEditImageId] = useState<string | null>(null); // Track the image being edited
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [selectedImage, setSelectedImage] = useState<ImageDTO | null>(null);
  const [deletedImageIds, setDeletedImageIds] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showDeleteAllConfirmation, setShowDeleteAllConfirmation] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [isADetailerModalOpen, setIsADetailerModalOpen] = useState(false);
  const [isFaceSwapModalOpen, setIsFaceSwapModalOpen] = useState(false);
  const [isBGRemovalModalOpen, setIsBGRemovalModalOpen] = useState(false);
  const [isCanvasModalOpen, setCanvasModalOpen] = useState<boolean>(false);
  const [isInpaintingModalOpen, setInpaintingModalOpen] = useState(false);
  const [base64Image, setBase64Image] = useState<string | null>(null);
  const [editImage, setEditImage] = useState<ImageDTO | null>(null);
  const [isUpscalerModalOpen, setUpscalerModalOpen] = useState(false);
  const handleCloseUpscalerModal = () => {
    setUpscalerModalOpen(false);
  };
  const handleUpscalerModalClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      const editedImage = images.find(image => image.id === imageId);
      if (editedImage && editedImage.imageMetadata.npcFieldsMap["Edit Type"] === "UPSCALER") {
        toast.error("This image has already been upscaled and cannot be upscaled again.");
      } else if (imageId) {
        setEditImageId(imageId);
        setUpscalerModalOpen(true);
      }
    }
  };
  const handleInpaintClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      const editedImage = images.find(image => image.id === imageId);
      if (editedImage) {
        setEditImage(editedImage);
      }
      setEditImageId(imageId);
      setCanvasModalOpen(true);
    }
  };
  const handleCloseCanvasModal = () => {
    setCanvasModalOpen(false);
  };
  const handleMaskSubmit = (maskData: string) => {
    setBase64Image(maskData); // Store the base64 data from the canvas
    setCanvasModalOpen(false); // Close the canvas modal
    setInpaintingModalOpen(true); // Open the inpainting modal
  };
  const handleCloseInpaintingModal = () => {
    setInpaintingModalOpen(false);
    setBase64Image(null); // Clear the image data
  };
  const handleCloseBGRemovalModal = () => {
    setIsBGRemovalModalOpen(false);
  };
  const handleBGRemovalClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsBGRemovalModalOpen(true);
    }
  };
  const handleShareClick = (e: React.MouseEvent, imageId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedImage = images.find(image => image.id === imageId);
    if (selectedImage) {
      setSelectedImage(selectedImage);
      setIsShareModalOpen(true);
    }
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  const [currentImageId, setCurrentImageId] = useState<string | null>(null);
  const handleCompetitionsClick = (imageId: string) => {
    setCurrentImageId(imageId);
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    setCurrentImageId(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleSelectImage = (imageId: string) => {
    setIsSelectionMode(true); // Automatically enable selection mode
    setSelectedImages(prev => {
      const newSelectedImages = new Set(prev);
      if (newSelectedImages.has(imageId)) {
        newSelectedImages.delete(imageId);
      } else {
        newSelectedImages.add(imageId);
      }
      return newSelectedImages;
    });
  };
  const handleDeselectAll = () => {
    setIsSelectionMode(false);
    setSelectedImages(new Set());
  };
  const handleDeleteAll = async () => {
    try {
      const dataPayload: IDeleteImageRequest = {
        imageIds: Array.from(selectedImages)
      };
      await deleteAllImages(dataPayload);
      setDeletedImageIds(prev => {
        const newDeletedImageIds = new Set(prev);
        selectedImages.forEach(id => newDeletedImageIds.add(id));
        return newDeletedImageIds;
      });
      setImages(prevImages => prevImages.filter(image => !selectedImages.has(image.id)));
      setShowDeleteAllConfirmation(false);
      handleDeselectAll();
      dispatch({
        type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
        payload: {
          ...state,
          isOverlayVisible: false
        }
      });
    } catch (error: any) {
      captureException("Failed to delete images", error);
      setShowDeleteAllConfirmation(false);
      handleDeselectAll();
    }
  };
  const handleEditSubmit = (editRequest: string, isPremium: boolean, editType: string, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File, base64Image?: string) => {
    if (editImageId) {
      onSubmitEdit(editRequest, isPremium, editImageId, editType, negativePrompt, cfgScale, numDetections, urlOrFile, base64Image);
    }
    setShowEditModal(false);
  };
  const handleCloseFaceSwapModal = () => {
    setIsFaceSwapModalOpen(false);
  };
  const handleFaceSwapClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsFaceSwapModalOpen(true);
    }
  };
  const handleADetailerClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setIsADetailerModalOpen(true);
    }
  };
  const handleCloseADetailerModal = () => {
    setIsADetailerModalOpen(false);
  };
  const handleEditCancel = () => {
    setShowEditModal(false);
  };
  const handleEditClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      setEditImageId(imageId);
      setShowEditModal(true);
    }
  };
  useEffect(() => {
    if (imageDtos) {
      const mergedImages = imageDtos.filter(dto => !deletedImageIds.has(dto.id)).map(dto => ({
        ...dto,
        isVotedByUser: images.find(img => img.id === dto.id)?.isVotedByUser ?? dto.isVotedByUser,
        voteCount: images.find(img => img.id === dto.id)?.voteCount ?? dto.voteCount
      }));
      setImages(mergedImages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageDtos]);
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const currentImage = images.find(img => img.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.isVotedByUser;
    try {
      await onVote(imageId);
      setImages(prevImages => prevImages.map(img => img.id === imageId ? {
        ...img,
        isVotedByUser: newVoteStatus,
        voteCount: img.voteCount + (newVoteStatus ? 1 : -1)
      } : img));
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  const prepareForDelete = (imageId: string) => {
    setImageToDelete(imageId);
    setShowDeleteConfirmation(true);
  };
  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;
    try {
      await deleteImage(imageToDelete);
      setDeletedImageIds(prev => new Set(prev).add(imageToDelete));
      setImages(prevImages => prevImages.filter(img => img.id !== imageToDelete));
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
      dispatch({
        type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
        payload: {
          ...state,
          isOverlayVisible: false
        }
      });
    } catch (error: any) {
      captureException("Failed to delete image", error);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setImageToDelete(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const isUserImage = (imageUsername: string) => state.username === imageUsername;
  const [toggles, setToggles] = useState<{
    [key: string]: boolean;
  }>(imageDtos.reduce((acc, image) => ({
    ...acc,
    [image.id]: image.isPublic
  }), {}));
  useEffect(() => {
    setToggles(imageDtos.reduce((acc, image) => ({
      ...acc,
      [image.id]: image.isPublic
    }), {}));
  }, [imageDtos]);
  const imageUrls = imageDtos.map(dto => dto.image);
  const ImageContainer = imageUrls.length > 1 ? Styled.ImageGridContainer : Styled.ImageContainer;
  useEffect(() => {
    const userImages = images.filter(img => img.imageMetadata.username === state.username);
    const newToggles = userImages.reduce((acc, image) => ({
      ...acc,
      [image.id]: image.isPublic
    }), {});
    setToggles(newToggles);
    const updatedImages = images.map(img => {
      const userImg = userImages.find(uImg => uImg.id === img.id);
      return userImg ? {
        ...img,
        isPublic: userImg.isPublic,
        voteCount: userImg.voteCount,
        isVotedByUser: userImg.isVotedByUser
      } : img;
    });
    setImages(updatedImages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.username]);

  // useEffect(() => {
  //     setImages(imageDtos);
  // }, [imageDtos]);

  const handleToggle = (imageId: string) => {
    const currentVisibility = toggles[imageId];
    const newVisibility = !currentVisibility;
    onToggleVisibility(imageId, newVisibility).then(() => {
      setToggles(prev => ({
        ...prev,
        [imageId]: newVisibility
      }));
    }).catch(error => {
      captureException("Failed to update visibility status.", error);
    });
  };
  return <>
            {!!images.length && <ImageContainer>
                    {images.map((image: ImageDTO, index: number) => <Styled.ImageWrapper key={image.id}>
                            <Styled.StyledLazyLoadImage src={image.image} alt={`Generated artwork ${index + 1}`} loading="lazy" onClick={() => isSelectionMode ? handleSelectImage(image.id) : onImageClick(image)} isSelected={selectedImages.has(image.id)} />
                            <Checkbox type="checkbox" checked={selectedImages.has(image.id)} onChange={() => handleSelectImage(image.id)} className={isSelectionMode || selectedImages.has(image.id) ? "visible" : ""} id={`checkbox-${image.id}`} variant={CheckboxType.ERROR} />

                            <label htmlFor={`checkbox-${image.id}`}></label>
                            <ControlsContainer imageInfo={image} toggles={toggles} handleToggle={handleToggle} handleShareClick={handleShareClick} handleCompetitionsClick={() => handleCompetitionsClick(image.id)} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscalerModalClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isUserImage={isUserImage} />
                        </Styled.ImageWrapper>)}
                    {!isSelectionMode && images.length > 0 && <Styled.ButtonContainer>
                            <Button variant={ButtonType.PRIMARY} onClick={() => setIsSelectionMode(true)}>
                                Enable Selection Mode
                            </Button>
                        </Styled.ButtonContainer>}
                    {isSelectionMode && <Styled.ButtonContainer>
                            <Button variant={ButtonType.PRIMARY} onClick={() => setShowDeleteAllConfirmation(true)}>
                                Delete Selected
                            </Button>
                            <Button variant={ButtonType.ERROR} onClick={handleDeselectAll}>
                                Cancel
                            </Button>
                        </Styled.ButtonContainer>}
                    <DeleteAllConfirmationModal isOpen={showDeleteAllConfirmation} onConfirm={handleDeleteAll} onCancel={() => setShowDeleteAllConfirmation(false)} deleteCount={selectedImages.size} />
                    <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" />
                    {editImageId && <EditModal isOpen={showEditModal} onCancel={handleEditCancel} onSubmit={handleEditSubmit} imageId={editImageId} />}
                    <ShareModal isOpen={isShareModalOpen} image={selectedImage} onClose={handleCloseShareModal} />
                    {currentImageId && <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={currentImageId} />}
                    {editImageId && <ADetailerModal isOpen={isADetailerModalOpen} onClose={handleCloseADetailerModal} onSubmit={handleEditSubmit} imageId={editImageId} isPremium={false} />}
                    {editImageId && <FaceSwapModal isOpen={isFaceSwapModalOpen} onClose={handleCloseFaceSwapModal} onSubmit={handleEditSubmit} imageId={editImageId} />}
                    {editImageId && <BGRemovalModal isOpen={isBGRemovalModalOpen} onClose={handleCloseBGRemovalModal} onSubmit={handleEditSubmit} imageId={editImageId} />}
                    {editImageId && <CanvasModal isOpen={isCanvasModalOpen} onClose={handleCloseCanvasModal} imageDto={editImage} onMaskSubmit={handleMaskSubmit} />}
                    {editImageId && editImage && <InpaintingModal isOpen={isInpaintingModalOpen} onCancel={handleCloseInpaintingModal} base64Image={base64Image!} originalImage={editImage.image} imageId={editImageId} handleInpaintClick={handleInpaintClick} onSubmit={handleEditSubmit} />}
                    {editImageId && <UpscalerModal isOpen={isUpscalerModalOpen} onClose={handleCloseUpscalerModal} imageId={editImageId} onSubmit={handleEditSubmit} />}
                </ImageContainer>}
        </>;
};